'use client';

import Image from 'next/image';
import Link from 'next/link';
import { strapiImage } from '@/lib/strapi/strapiImage';

interface ResourceBlock {
	id: number;
	title: string;
	image: {
		id: number;
		url: string;
		alternativeText: string | null;
		width: number;
		height: number;
		name: string;
	};
	link: string;
}

interface BlogsResourcesProps {
	id: number;
	heading: string;
	resource_blocks: ResourceBlock[];
	__component: string;
	isFirst: boolean;
}

const BlogsResources = ({
	heading,
	resource_blocks,
	isFirst,
}: BlogsResourcesProps) => {
	if (!resource_blocks || resource_blocks.length === 0) {
		console.warn('No resource blocks provided');
		return null;
	}

	return (
		<section
			role="region"
			className="blogs-resources-section w-full bg-white py-16 md:py-24">
			<div className="container mx-auto px-4 md:px-6">
				{/* Heading */}
				{isFirst ? (
					<h1 className="mb-12 text-center text-3xl font-bold tracking-tight text-gray-900 md:mb-16 md:text-4xl">
						{heading}
					</h1>
				) : (
					<h2 className="mb-12 text-center text-3xl font-bold tracking-tight text-gray-900 md:mb-16 md:text-4xl">
						{heading}
					</h2>
				)}

				{/* Resource Blocks Grid */}
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 md:gap-8">
					{resource_blocks.map((block, index) => (
						<Link
							key={block.id || index}
							href={block.link}
							className="group overflow-hidden rounded-2xl bg-gray-50 p-4 transition-all duration-200 hover:shadow-xl hover:shadow-indigo-600/20">
							{/* Image Container */}
							<div
								data-testid="image-container"
								className="relative mb-4 aspect-video w-full overflow-hidden rounded-xl">
								<Image
									src={strapiImage(block.image.url)}
									alt={
										block.image.alternativeText ||
										block.title
									}
									fill
									className="object-fit transition-transform duration-300 group-hover:scale-105"
									sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
								/>
							</div>

							{/* Title */}
							<h3 className="text-center text-lg font-semibold text-gray-900 group-hover:text-indigo-600">
								{block.title}
							</h3>
						</Link>
					))}
				</div>
			</div>
		</section>
	);
};

export default BlogsResources;
